// Per-page setting builders for the generic SettingDetail overlay — the
// same meaning/situation/recommend/evidence shape as exchangeSettings.js,
// just for the other pages that have a "Nuværende opsætning" section.
// Each builder takes whatever real data SettingDetail.jsx has already
// loaded (via useTenantData + the identity-risks fetch every one of these
// pages already makes) and returns { [settingKey]: {...} } — undefined
// fields simply aren't shown rather than guessed at.

import { hasLicenseFor, licenseGateSetting } from './licenseGate';

const SHARING_LABEL = {
  externalUserAndGuestSharing: 'Alle med linket kan få adgang — også uden at logge ind',
  externalUserSharingOnly: 'Eksterne skal logge ind eller bekræfte deres identitet',
  existingExternalUserSharingOnly: 'Kun eksisterende gæster kan få adgang',
  disabled: 'Ekstern deling er slået fra',
};
const DOMAIN_RESTRICTION_LABEL = { none: 'Ingen', allowList: 'Kun tilladte domæner', blockList: 'Blokerede domæner' };
const INVITE_POLICY_LABEL = {
  none: 'Ingen kan invitere',
  adminsAndGuestInviters: 'Kun admins og udpegede',
  adminsGuestInvitersAndAllMembers: 'Alle medarbejdere',
  everyone: 'Alle, inkl. gæster',
};

function buildSharePointSettings(ctx) {
  const s = ctx.sharePointSettings;
  if (!s) return {};
  const openSharing = s.sharingCapability === 'externalUserAndGuestSharing';
  const restricted = !!s.sharingDomainRestrictionMode && s.sharingDomainRestrictionMode !== 'none';
  return {
    sharing: {
      icon: 'ED',
      title: 'Ekstern deling',
      description: 'Hvem uden for organisationen kan få adgang til delte filer',
      value: SHARING_LABEL[s.sharingCapability] || s.sharingCapability,
      tone: s.sharingCapability === 'disabled' ? 'good' : openSharing ? 'bad' : 'warn',
      meaning: 'Bestemmer, om og hvordan personer uden for organisationen kan få adgang til filer og sites i SharePoint.',
      situation: openSharing
        ? 'Indstillingen er den mest åbne: alle med et link kan få adgang, også uden at logge ind.'
        : `Jeres indstilling er sat til "${SHARING_LABEL[s.sharingCapability] || s.sharingCapability}".`,
      recommend: openSharing
        ? 'Overvej "Eksterne skal logge ind eller bekræfte deres identitet", hvis I vil vide, hvem der åbner filerne.'
        : null,
      evidence: [
        ['Observed value', s.sharingCapability || 'Ikke tilgængeligt endnu'],
        ['Source', 'SharePoint Online / Graph'],
        ['Collected at', ctx.checkedAt],
      ],
    },
    resharing: {
      icon: 'GD',
      title: 'Gendeling for eksterne',
      description: 'Om eksterne selv kan dele videre til andre',
      value: s.isResharingByExternalUsersEnabled ? 'Ja' : 'Nej',
      tone: s.isResharingByExternalUsersEnabled ? 'warn' : 'good',
      meaning: 'Bestemmer, om en ekstern person, der allerede har fået adgang, selv kan give andre adgang.',
      situation: s.isResharingByExternalUsersEnabled
        ? 'Eksterne, der har fået adgang, kan selv dele videre til andre. Det kan gøre det sværere at se, hvem der har adgang.'
        : 'Eksterne kan ikke dele videre til andre på egen hånd.',
      recommend: s.isResharingByExternalUsersEnabled ? 'Overvej at slå gendeling fra, hvis kun I selv skal give adgang.' : null,
      evidence: [
        ['Observed value', String(!!s.isResharingByExternalUsersEnabled)],
        ['Source', 'SharePoint Online / Graph'],
        ['Collected at', ctx.checkedAt],
      ],
    },
    domainRestriction: {
      icon: 'DB',
      title: 'Domæne-begrænsning',
      description: 'Hvilke domæner der kan inviteres som eksterne',
      value: DOMAIN_RESTRICTION_LABEL[s.sharingDomainRestrictionMode] || s.sharingDomainRestrictionMode || 'Ingen',
      tone: restricted ? 'good' : 'warn',
      meaning: 'Bestemmer, hvilke e-maildomæner der kan inviteres som eksterne. Uden en begrænsning kan alle domæner inviteres.',
      situation: restricted
        ? `Kun domæner på jeres "${DOMAIN_RESTRICTION_LABEL[s.sharingDomainRestrictionMode]}"-liste kan inviteres.`
        : 'Der er ingen begrænsning på domæner. Alle e-mailadresser kan inviteres.',
      recommend: restricted ? null : 'Overvej en liste over tilladte domæner, hvis I kun samarbejder med kendte partnere.',
      evidence: [
        ['Observed value', s.sharingDomainRestrictionMode || 'none'],
        ['Source', 'SharePoint Online / Graph'],
        ['Collected at', ctx.checkedAt],
      ],
    },
    legacyAuth: {
      icon: 'LA',
      title: 'Ældre godkendelse',
      description: 'Om forældede login-protokoller uden MFA-understøttelse er tilladt',
      value: s.isLegacyAuthProtocolsEnabled ? 'Slået til' : 'Slået fra',
      tone: s.isLegacyAuthProtocolsEnabled ? 'bad' : 'good',
      meaning: 'Ældre login-metoder understøtter ikke MFA (totrinsgodkendelse) og kan bruges i angreb med gættede eller stjålne adgangskoder.',
      situation: s.isLegacyAuthProtocolsEnabled
        ? 'Ældre godkendelsesprotokoller er slået til for SharePoint.'
        : 'Ældre godkendelsesprotokoller er slået fra for SharePoint.',
      recommend: s.isLegacyAuthProtocolsEnabled ? 'Overvej at slå ældre godkendelse fra, så logins sker med moderne metoder, der understøtter MFA.' : null,
      evidence: [
        ['Observed value', String(!!s.isLegacyAuthProtocolsEnabled)],
        ['Source', 'SharePoint Online / Graph'],
        ['Collected at', ctx.checkedAt],
      ],
    },
  };
}

function buildApplicationsSettings(ctx) {
  const allowed = ctx.applications?.userConsentAllowed;
  return {
    userConsent: {
      icon: 'UC',
      title: 'Brugere kan selv godkende apps',
      description: 'Om medarbejdere kan give tredjeparts-apps adgang uden admin-godkendelse',
      value: allowed === null ? 'Kan ikke hentes' : allowed ? 'Tilladt' : 'Kræver admin-godkendelse',
      tone: allowed === null ? 'info' : allowed ? 'bad' : 'good',
      meaning: 'Bestemmer, om en almindelig bruger selv kan give en ny app adgang til data i Microsoft 365.',
      situation:
        allowed === null
          ? 'Kunne ikke hentes for denne tenant.'
          : allowed
          ? 'Brugere kan selv give nye apps adgang, uden at en administrator godkender det.'
          : 'Nye apps kræver en administrators godkendelse, før de får adgang.',
      recommend: allowed ? 'Overvej at kræve admin-godkendelse, så ingen app får adgang uden, at en administrator har set på den.' : null,
      evidence: [
        ['Observed value', allowed === null ? 'Ikke tilgængeligt endnu' : String(allowed)],
        ['Source', 'Microsoft Entra / Graph'],
        ['Collected at', ctx.checkedAt],
      ],
    },
  };
}

function buildGuestUsersSettings(ctx) {
  const policy = ctx.risks?.guestInvitePolicy;
  const label = policy ? INVITE_POLICY_LABEL[policy] || policy : 'Kan ikke hentes';
  const risky = policy === 'everyone' || policy === 'adminsGuestInvitersAndAllMembers';
  return {
    invitePolicy: {
      icon: 'IP',
      title: 'Hvem kan invitere gæster',
      description: 'Hvem i organisationen kan invitere eksterne ind som gæstebrugere',
      value: label,
      tone: policy === 'everyone' ? 'bad' : policy === 'adminsGuestInvitersAndAllMembers' ? 'warn' : policy ? 'good' : 'info',
      meaning: 'Bestemmer, hvem i organisationen der kan invitere en ekstern person ind som gæst.',
      situation:
        policy === 'everyone'
          ? 'Alle, også eksisterende gæster, kan invitere nye eksterne personer.'
          : policy === 'adminsGuestInvitersAndAllMembers'
          ? 'Alle medarbejdere kan invitere gæster, uden at en administrator godkender det først.'
          : policy
          ? `Kun "${label}" kan invitere gæster ind.`
          : 'Kunne ikke hentes for denne tenant.',
      recommend: risky ? 'Overvej, om kun administratorer og udpegede personer skal kunne invitere gæster.' : null,
      evidence: [
        ['Observed value', policy || 'Ikke tilgængeligt endnu'],
        ['Source', 'Microsoft Entra / Graph'],
        ['Collected at', ctx.checkedAt],
      ],
    },
  };
}

function buildIdentitySettings(ctx) {
  const policy = ctx.risks?.guestInvitePolicy;
  const label = policy ? INVITE_POLICY_LABEL[policy] || policy : 'Kan ikke hentes';
  const risky = policy === 'everyone' || policy === 'adminsGuestInvitersAndAllMembers';
  return {
    invitePolicy: {
      icon: 'IP',
      title: 'Hvem kan invitere gæster',
      description: 'Hvem i organisationen kan invitere eksterne ind som gæstebrugere',
      value: label,
      tone: policy === 'everyone' ? 'bad' : policy === 'adminsGuestInvitersAndAllMembers' ? 'warn' : policy ? 'good' : 'info',
      meaning: 'Bestemmer, hvem i organisationen der kan invitere en ekstern person ind som gæst.',
      situation:
        policy === 'everyone'
          ? 'Alle, også eksisterende gæster, kan invitere nye eksterne personer.'
          : policy === 'adminsGuestInvitersAndAllMembers'
          ? 'Alle medarbejdere kan invitere gæster, uden at en administrator godkender det først.'
          : policy
          ? `Kun "${label}" kan invitere gæster ind.`
          : 'Kunne ikke hentes for denne tenant.',
      recommend: risky ? 'Overvej, om kun administratorer og udpegede personer skal kunne invitere gæster.' : null,
      evidence: [
        ['Observed value', policy || 'Ikke tilgængeligt endnu'],
        ['Source', 'Microsoft Entra / Graph'],
        ['Collected at', ctx.checkedAt],
      ],
    },
    securityDefaults: (() => {
      const enabled = ctx.risks?.securityDefaultsEnabled;
      const has = typeof enabled === 'boolean';
      // Security Defaults being off is only a problem when nothing else makes MFA required. So the verdict reads the
      // tenant's own Loginregler: an enabled rule that requires MFA, and whether one of them covers all users.
      const caKnown = Array.isArray(ctx.ca?.policies);
      const mfaRules = caKnown ? ctx.ca.policies.filter((p) => p.state === 'enabled' && ((p.grantControls?.builtInControls || []).includes('mfa') || p.authStrength)) : [];
      const allUsers = mfaRules.some((p) => p.includeUsers === 'Alle brugere');
      let tone = 'info';
      let situation = 'Kunne ikke hentes for denne tenant.';
      let recommend = null;
      if (has && enabled) {
        tone = 'good';
        situation = 'Security Defaults er aktiveret for denne tenant.';
      } else if (has && !caKnown) {
        tone = 'warn';
        situation = 'Security Defaults er deaktiveret, og vi kunne ikke læse jeres Loginregler, så vi kan ikke se, om MFA er påkrævet på anden vis.';
        recommend = 'Tjek under "Loginregler", at der er regler, der kræver MFA for alle brugere.';
      } else if (has && mfaRules.length === 0) {
        tone = 'bad';
        situation = 'Security Defaults er deaktiveret, og ingen aktive Loginregler kræver MFA. Tenanten har dermed ingen grundlæggende MFA-beskyttelse.';
        recommend = 'Slå Security Defaults til, eller opret en Loginregel, der kræver MFA for alle brugere.';
      } else if (has && allUsers) {
        tone = 'good';
        situation = `Security Defaults er deaktiveret. ${mfaRules.length} aktive Loginregler kræver MFA, blandt andet for alle brugere.`;
      } else if (has) {
        tone = 'warn';
        situation = `Security Defaults er deaktiveret. ${mfaRules.length} aktive Loginregler kræver MFA, men ingen af dem gælder alle brugere.`;
        recommend = 'Bekræft, at reglerne dækker alle brugere og administratorer, eller slå Security Defaults til, og dokumentér valget.';
      }
      return {
        icon: 'SD',
        title: 'Security Defaults',
        description: 'Microsofts tenant-brede sikkerhedsbaseline (adskilt fra jeres egne login-regler)',
        value: has ? (enabled ? 'Aktiveret' : 'Deaktiveret') : 'Kan ikke hentes',
        tone,
        meaning: 'Security Defaults er en samling grundlæggende sikkerhedskrav fra Microsoft, fx MFA for administratorer. Den kan erstattes af jeres egne Loginregler (Conditional Access).',
        situation,
        recommend,
        evidence: [
          ['Observed value', has ? String(enabled) : 'Ikke tilgængeligt endnu'],
          ...(caKnown ? [['Loginregler med MFA', String(mfaRules.length)]] : []),
          ['Source', 'Microsoft Entra / Graph'],
          ['Collected at', ctx.checkedAt],
        ],
      };
    })(),
    sessionTimeout: (() => {
      const has = typeof ctx.risks?.hasSessionTimeoutPolicy === 'boolean';
      const hasPolicy = ctx.risks?.hasSessionTimeoutPolicy;
      return {
        icon: 'ST',
        title: 'Session-timeout-politik',
        description: 'Om der er sat en tilpasset inaktivitets-timeout for browser-sessioner',
        value: has ? (hasPolicy ? 'Tilpasset politik findes' : 'Standard (ingen tilpasset timeout)') : 'Kan ikke hentes',
        tone: has ? (hasPolicy ? 'good' : 'info') : 'info',
        meaning: 'Bestemmer, hvor længe en bruger kan være logget ind i browseren uden at gøre noget. Uden en egen politik gælder Microsofts standard.',
        situation: has
          ? hasPolicy
            ? 'Der er konfigureret en tilpasset session-timeout-politik for denne tenant.'
            : 'Der er ingen tilpasset session-timeout. Microsofts standard gælder.'
          : 'Kunne ikke hentes for denne tenant.',
        recommend: 'Kræv en kortere sessionstid for administratorer, og slå "bliv logget ind" fra på enheder, I ikke administrerer.',
        evidence: [
          ['Observed value', has ? String(hasPolicy) : 'Ikke tilgængeligt endnu'],
          ['Source', 'Microsoft Entra / Graph'],
          ['Collected at', ctx.checkedAt],
        ],
      };
    })(),
    namedLocations: (() => {
      const count = ctx.risks?.namedLocationsCount;
      const trusted = ctx.risks?.trustedLocationsCount;
      const has = typeof count === 'number';
      return {
        icon: 'NL',
        title: 'Navngivne lokationer',
        description: 'IP-lister og lande, jeres login-regler kan bruge som betingelse',
        value: has ? count : 'Kan ikke hentes',
        tone: 'info',
        meaning: 'En navngivet lokation er en liste, I selv opretter, over IP-adresser eller lande — fx jeres kontors offentlige IP. En loginregel kan bruge den som betingelse, fx "kræv MFA, når login sker uden for kontorets IP" eller "bloker login fra lande, I ikke opererer i". Uden mindst én liste kan den slags regler ikke laves.',
        situation: has
          ? `I har oprettet ${count} navngivne ${count === 1 ? 'lokation' : 'lokationer'}${trusted ? ` — ${trusted} af dem er markeret som "betroet" (login herfra regnes som mindre risikabelt, fx fordi det er jeres eget kontor)` : ', ingen af dem markeret som "betroet"'}.`
          : 'Kunne ikke hentes for denne tenant.',
        recommend: 'Slå betroede lokationer til: Opret kontorets IP-adresse som betroet lokation, og kræv MFA, når login sker uden for den.',
        evidence: [
          ['Observed value', has ? String(count) : 'Ikke tilgængeligt endnu'],
          ['Source', 'Microsoft Entra / Graph'],
          ['Collected at', ctx.checkedAt],
        ],
      };
    })(),
    passwordExpiry: (() => {
      const days = ctx.risks?.passwordValidityDays;
      const never = ctx.risks?.passwordNeverExpires;
      const has = typeof never === 'boolean';
      return {
        icon: 'PW',
        title: 'Adgangskode-udløb',
        description: 'Om adgangskoder udløber automatisk for denne tenant',
        value: has ? (never ? 'Udløber aldrig' : `${days} dage`) : 'Kan ikke hentes',
        tone: 'info',
        meaning: 'Viser, om og hvornår adgangskoder udløber. Krav til længde og kompleksitet kan vi ikke læse, og en eventuel adgangskodepolitik i en lokal Active Directory kan vi heller ikke se.',
        situation: has
          ? never
            ? 'Adgangskoder er sat til aldrig at udløbe.'
            : `Adgangskoder udløber automatisk efter ${days} dage.`
          : 'Kunne ikke hentes for denne tenant.',
        recommend: 'Lad adgangskoder være uden udløb, og kræv MFA i stedet. Det anbefaler både Microsoft og NIST.',
        evidence: [
          ['Observed value', has ? (never ? 'never' : String(days)) : 'Ikke tilgængeligt endnu'],
          ['Source', 'Microsoft Entra / Graph'],
          ['Collected at', ctx.checkedAt],
        ],
      };
    })(),
  };
}

function buildDevicesSettings(ctx) {
  const hasIntune = hasLicenseFor(ctx.resources?.licensedServicePlans, 'intune');
  if (hasIntune === false) {
    return {
      compliancePolicies: licenseGateSetting({
        icon: 'CP',
        title: 'Compliance-politikker',
        description: 'Antal aktive Intune-politikker der reelt tjekker enhedernes sikkerhed',
        groupKey: 'intune',
        evidenceSchema: 'device-governance.1',
        checkedAt: ctx.checkedAt,
      }),
      encryptionRequired: licenseGateSetting({
        icon: 'EN',
        title: 'Diskkryptering påkrævet af politik',
        description: 'Om mindst én compliance-politik kræver diskkryptering (BitLocker/FileVault)',
        groupKey: 'intune',
        evidenceSchema: 'device-governance.1',
        checkedAt: ctx.checkedAt,
      }),
    };
  }

  const count = ctx.devices?.compliancePolicyCount;
  const has = typeof count === 'number';
  const encRequired = ctx.devices?.encryptionRequiredByPolicy;
  const hasEnc = typeof encRequired === 'boolean';

  return {
    compliancePolicies: {
      icon: 'CP',
      title: 'Compliance-politikker',
      description: 'Antal aktive Intune-politikker der reelt tjekker enhedernes sikkerhed',
      value: has ? count : 'Kan ikke hentes',
      tone: has && count === 0 ? 'bad' : has ? 'good' : 'info',
      meaning: 'En compliance-politik er et sæt regler, som en enhed skal opfylde i Intune, fx kryptering. Uden en politik kan Intune ikke vurdere, om en enhed er i orden.',
      situation:
        has && count === 0
          ? 'Der er ikke oprettet nogen compliance-politik i Intune.'
          : has
          ? `${count} ${count === 1 ? 'compliance-politik er' : 'compliance-politikker er'} aktive i Intune.`
          : 'Kunne ikke hentes for denne tenant.',
      recommend: has && count === 0 ? 'Overvej at oprette en compliance-politik i Intune.' : null,
      evidence: [
        ['Observed value', has ? String(count) : 'Ikke tilgængeligt endnu'],
        ['Source', 'Microsoft Intune / Graph'],
        ['Collected at', ctx.checkedAt],
      ],
    },
    encryptionRequired: {
      icon: 'EN',
      title: 'Diskkryptering påkrævet af politik',
      description: 'Om mindst én compliance-politik kræver diskkryptering (BitLocker/FileVault)',
      value: hasEnc ? (encRequired ? 'Ja' : 'Nej') : 'Kan ikke hentes',
      tone: hasEnc ? (encRequired ? 'good' : 'bad') : 'info',
      meaning: 'Kryptering af harddisken (BitLocker på Windows, FileVault på Mac) gør, at data på en tabt eller stjålet enhed ikke kan læses uden nøglen.',
      situation: hasEnc
        ? encRequired
          ? 'Mindst én compliance-politik kræver diskkryptering på enhederne.'
          : 'Ingen af jeres compliance-politikker kræver diskkryptering.'
        : 'Kunne ikke hentes for denne tenant.',
      recommend: hasEnc && !encRequired ? 'Overvej at tilføje et krav om diskkryptering til en compliance-politik.' : null,
      evidence: [
        ['Observed value', hasEnc ? String(encRequired) : 'Ikke tilgængeligt endnu'],
        ['Source', 'Microsoft Intune / Graph'],
        ['Collected at', ctx.checkedAt],
      ],
    },
    // "Enheder uden rapporteret sikkerhedsstatus" used to live here — removed: it's a per-device count,
    // the exact same number as Arbejdskø's own "Ingen rapporteret sikkerhedsstatus" finding (both read
    // noThreatAgentCount), not a tenant-wide setting. Same reasoning as Ekstern videresendelse above.
  };
}

function buildSecuritySettings(ctx) {
  // "Microsoft Defender-dækning" used to live here — removed: it's the exact same underlying number as
  // devices' own (also removed) noThreatAgentCount, just shown as a percentage — a per-device count, not
  // a tenant-wide setting, and Arbejdskø's own "Ingen rapporteret sikkerhedsstatus" finding already
  // covers it. Two rated rows for one number was also its own kind of duplication.
  return {
    autoInvestigation: {
      icon: 'AI',
      title: 'Automatisk undersøgelse & afhjælpning',
      description: 'Om Defender selv undersøger og lukker fund uden manuel indgriben',
      value: 'Ikke tilgængeligt endnu',
      tone: 'info',
      meaning: 'Lader Defender selv undersøge og lukke visse fund uden at vente på en administrator.',
      situation: 'Vi kan endnu ikke læse denne indstilling.',
      recommend: 'Se indstillingen i Microsoft Defender-portalen.',
      evidence: [
        ['Observed value', 'Ikke tilgængeligt endnu'],
        ['Source', 'Microsoft Defender'],
        ['Collected at', 'Ikke tilgængeligt endnu'],
      ],
    },
  };
}

function buildRolesSettings(ctx) {
  const globalAdmins = ctx.risks?.privilegedRoles?.filter((r) => r.role === 'Global Administrator') ?? null;
  const eligibleCount = globalAdmins?.filter((r) => r.status === 'eligible').length ?? null;
  const totalCount = ctx.risks?.privilegedRoles?.length ?? null;
  // "Permanente Global Administrator-tildelinger" used to live here — removed: it's a per-person count,
  // the exact same computation (and the same "excludes activated PIM" rule) as Arbejdskø's own
  // "Permanent Global Administrator" finding, not a tenant-wide setting. Same reasoning as the other
  // removed rows above.
  return {
    pimGa: {
      icon: 'PIM',
      title: 'PIM-berettigede tildelinger',
      description: 'Personer der kan aktivere Global Administrator, når de faktisk skal bruge den',
      value: eligibleCount ?? 'Kan ikke hentes',
      tone: 'good',
      meaning: 'PIM (Privileged Identity Management) lader en person slå en rolle til, når den skal bruges, i stedet for at have den hele tiden.',
      situation: `${eligibleCount ?? 0} ${eligibleCount === 1 ? 'person kan' : 'personer kan'} aktivere Global Administrator via PIM efter behov.`,
      recommend: null,
      evidence: [
        ['Observed value', String(eligibleCount ?? 'Ikke tilgængeligt endnu')],
        ['Source', 'Microsoft Entra / Graph'],
        ['Collected at', ctx.checkedAt],
      ],
    },
    totalAssignments: {
      icon: 'RT',
      title: 'Rolletildelinger i alt',
      description: 'Antal tildelinger på tværs af de roller der indgår i en compliance-gennemgang',
      value: totalCount ?? 'Kan ikke hentes',
      tone: 'info',
      meaning: 'Det samlede antal permanente og PIM-berettigede tildelinger af administratorroller.',
      situation: `${totalCount ?? 0} rolletildelinger i alt.`,
      recommend: 'Hold antallet lavt, gennemgå dem hvert kvartal, og brug PIM til de privilegerede roller.',
      evidence: [
        ['Observed value', String(totalCount ?? 'Ikke tilgængeligt endnu')],
        ['Source', 'Microsoft Entra / Graph'],
        ['Collected at', ctx.checkedAt],
      ],
    },
  };
}

const BUILDERS = {
  sharepoint: buildSharePointSettings,
  applications: buildApplicationsSettings,
  'guest-users': buildGuestUsersSettings,
  security: buildSecuritySettings,
  roles: buildRolesSettings,
  identity: buildIdentitySettings,
  devices: buildDevicesSettings,
};

export function buildSettingsFor(page, ctx) {
  const builder = BUILDERS[page];
  const settings = builder ? builder(ctx) : {};
  // SettingsList/SettingDetail both key off item.key — add it here, once,
  // from each entry's own map key, rather than repeating it by hand in
  // every builder above (exchangeSettings.js does that inline since it's
  // the one page that predates this registry).
  Object.entries(settings).forEach(([key, setting]) => {
    setting.key = key;
  });
  return settings;
}
